import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Chip,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
  Collapse
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { INDICATOR_MAP } from "../../utils/indicatorMap";
import StyledTooltip from "../../utils/styledToolTip";
import { formatDecimal } from "../../utils/numberFormat";

export default function ScoresTable({projects, sectionTitle, forPdf=false}) {
  const [openRowKey, setOpenRowKey] = useState(null);

  const toggle = (key) => {
    setOpenRowKey((prev) => (prev === key ? null : key));
  }
  
  const onKeyDown = (key) => (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      toggle(key);
    }
  };

  const formatWithSpaceThousands = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "-";
    return formatDecimal(value);
  }

  // Helper para encontrar a secção pelo título
  const findSectionByTitle = (indicatorMap, title) =>
    Array.isArray(indicatorMap)
      ? indicatorMap.find(
          (s) =>
            s &&
            typeof s.title === "string" &&
            s.title.trim().toLowerCase() === String(title).trim().toLowerCase()
        )
      : undefined;

  // Recebe o INDICATOR_MAP e devolve os indicators da secção "Mobility scores"
  const getIndicatorsFromSection = (indicatorMap, title) => {
    const section = findSectionByTitle(indicatorMap, title);
    return Array.isArray(section?.indicators) ? section.indicators : [];
  };

  const section = findSectionByTitle(INDICATOR_MAP, sectionTitle);
  const indicators = getIndicatorsFromSection(INDICATOR_MAP, sectionTitle); //"Mobility scores"

  if (!INDICATOR_MAP) return null;
  const { name, unit, value, subIndicators = [] } = INDICATOR_MAP;

  /* Badge “out of X” */
  function IndicatorBadge({ max }) {
    return (
      <Chip
        label={`${max}`}
        size="small"
        variant="outlined"
        sx={{
          ml: 1,
          fontSize: 12,
          height: 28,
          borderRadius: "6px",
          color: "#bdbdbd",
          "& .MuiChip-label": { px: 1.0 },
        }}
      />
    );
  }

  const roundToTwo = (value) => {
    if (value === null || value === undefined) return '\u00A0'; // NBSP
    return Math.round((value + Number.EPSILON) * 100) / 100;
  }

  return (
    //  {/* Scores */}
    <Box sx={{ mt: forPdf ? 1.5 : 3, pageBreakBefore: 'auto' }}>
      <Box display="flex" alignItems="center">
        <Typography variant="h6">{sectionTitle}</Typography>
        <Typography
          component="span"
          variant="body2"
          color="text.secondary"
          sx={{ ml: 1, cursor: "help" }}
        >
        <StyledTooltip title={section.titleTooltip} arrow><span className="icon"></span></StyledTooltip>
        </Typography>
      </Box>

      <Table stickyHeader sx={{ mt: forPdf ? 0.5 : 1.5, tableLayout: "fixed" }}>
        <TableHead>
          <TableRow
            sx={{
              position: "relative",
              "& > .MuiTableCell-root": {
                borderBottom: "1px solid #e0e0e0 !important",
                borderCollapse: "collapse",
              },
            }}>
            <TableCell sx={{ width: "100%", pl:0 }}>
              <Typography
                variant="subtitle1"
                color="text.secondary"
                align="left"
                sx={{ fontWeight: 600, px: 0 }}>
                Indicator
              </Typography>
            </TableCell>

            {/* Gera uma coluna por projeto */}
            {projects.map((project, idx) => (
              <TableCell
                key={project.id ?? project.projectName}
                align="right"
                sx={{ width: 160, maxWidth: 160, px: 0 }}>
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  align="center"
                  sx={{ fontWeight: 600, px: 1 }}>
                  {project.projectName}
                </Typography>
              </TableCell>
            ))}
            <TableCell sx={{ width: 80, maxWidth: 80 }}></TableCell>
          </TableRow>
        </TableHead>

         <TableBody>
          {indicators.map((indicator) => {
            
            const hasSubIndicators = Array.isArray(indicator.subIndicators) && 
              indicator.subIndicators.length > 0;
            const isOpen = hasSubIndicators && (forPdf || openRowKey === indicator.key);

            return (
              <React.Fragment key={indicator.key}>

              <TableRow
                hover={true} //isExpandable
                //onClick={toggle}
                onClick={hasSubIndicators ? () => toggle(indicator.key) : undefined}
                //onKeyDown={onKeyDown}
                onKeyDown={hasSubIndicators ? onKeyDown(indicator.key) : undefined}
                role={hasSubIndicators ? "button" : undefined}
                tabIndex={hasSubIndicators ? 0 : undefined}
                aria-expanded={hasSubIndicators ? isOpen : undefined}
                aria-label={"details"} //`${indicator.label} details`
                sx={{
                  cursor: "pointer",
                  //cursor: hasAnyKeysForSection(projects, sectionTitle, indicator) ? "pointer" : "default",
                  outline: "none",
                  "&:focus-visible": (theme) => ({
                    boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}33`,
                  }),
                }}>
                <TableCell
                  sx={{
                    width: "calc(100% - " + projects.length * 160 + "px )",
                    py: 1.25,
                    borderBottom: "1px solid #e0e0e0 !important",
                  }}>
                  <Box sx={{ 
                    display: "flex", alignItems: "center", gap: 1
                    }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, width: '200px' }}>
                      {indicator.name} {/*Retail Quality*/} 
                    </Typography>

                    <Box sx={{ 
                      display: "flex", alignItems: "center", justifyContent: "center", gap: 1, width: '110px'
                    }}>
                      <IndicatorBadge max={indicator.unit} />
                    </Box>
                  </Box>
                </TableCell>

                {/* Colunas por projeto */}
                {projects.map((project) => (
                  <TableCell
                    key={project.id ?? project.projectName}
                    align="right"
                    sx={{
                      width: 160,
                      minWidth: 160,
                      maxWidth: 160,
                      borderBottom: "1px solid #e0e0e0 !important",
                    }}>
                    <Typography
                      variant="body2"
                      align="center"
                      noWrap
                      sx={{ fontVariantNumeric: "tabular-nums" }}
                      title={String("")}>
                      {roundToTwo(project.projectInfo[indicator.key])}
                    </Typography>
                  </TableCell>
                ))}

                <TableCell
                  aria-hidden
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "text.secondary",
                    pointerEvents: "none", // a linha continua a receber o clique
                    borderBottom: "1px solid #e0e0e0 !important",
                    py: hasSubIndicators ? 2 : 3.5, // 👈 menos padding sem arrow
                  }}>                
                    {hasSubIndicators && (
                      isOpen ? (
                        <KeyboardArrowUpIcon fontSize="small" />
                      ) : (
                        <KeyboardArrowDownIcon fontSize="small" />
                      )
                    )}
                </TableCell>
              </TableRow>
                            
                {/* ===== Linha “content” que colapsa e contém SubIndicators (só mostra se existirem )===== */}
                {hasSubIndicators && (
                <TableRow>
                  <TableCell
                    colSpan={projects.length + 2} // 1 para a 1ª célula + N projetos + 1 célula do ícone
                    sx={{ p: 0, borderBottom: "none" }}
                  >
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                      <Box>
                        {(indicator.subIndicators ?? []).map((sub) => (
                          <TableRow key={sub.key} sx={{ width: "100%", display: "inline-table"}}>
                            <TableCell
                              sx={{
                                pl: 6, // indent visual
                                py: 1,
                                borderBottom: "1px solid #f2f2f2 !important",
                                width: "100%"
                              }}
                            >
                              <Typography variant="body2" color="text.secondary">
                                {sub.name}
                              </Typography>
                            </TableCell>

                            {projects.map((project) => (
                              <TableCell
                                key={`${sub.key}-${project.id ?? project.projectName}`}
                                align="right"
                                sx={{
                                  width: 160,
                                  minWidth: 160,
                                  maxWidth: 160,
                                  borderBottom: "1px solid #f2f2f2 !important",
                                }}
                              >
                                {/* aqui podes renderizar o valor do sub-indicador por projeto */}
                                <Typography variant="body2" align="center" noWrap>
                                  {formatWithSpaceThousands(roundToTwo(project.projectInfo[sub.key]))}
                                </Typography>
                              </TableCell>
                            ))}

                            {/* célula “ícone” vazia para alinhamento */}
                            <TableCell
                              aria-hidden
                              sx={{
                                borderBottom: "1px solid #f2f2f2 !important", minWidth: "80px"
                              }}
                            />
                          </TableRow>
                        ))}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              )}
              </React.Fragment>
            );
          })}
        </TableBody>
        
        <TableFooter>
          <TableRow 
            sx={{
              backgroundColor: "#f5f5f5",      // 👈 fundo cinza claro como no mock
            }}
          >
            <TableCell
              sx={{
                width: "calc(100% - " + projects.length * 160 + "px )",
                py: 1.25,
                borderBottom: "1px solid #e0e0e0 !important",
              }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 700, width: '200px' }}>
                  {section.lastName}   {/*Mobility Combined Score*/}
                </Typography>
                <Box sx={{ 
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 1, width: '110px'
                }}>
                  <IndicatorBadge max={section.unit} />
                </Box>
              </Box>
            </TableCell>
            {/* Colunas por projeto */}
            {projects.map((project) => (
              <TableCell
                key={project.id ?? project.projectName}
                align="right"
                sx={{ 
                  width: 160,
                  minWidth: 160,
                  maxWidth: 160, 
                  borderBottom: "1px solid #e0e0e0 !important",
                }}>
                <Typography
                  variant="body2"
                  align="center"
                  noWrap
                  sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 800 }}
                  title={String("")}>
                  {roundToTwo(project.projectInfo[section.combined])}
                </Typography>
              </TableCell>
            ))}
            <TableCell
              aria-hidden
              sx={{
                borderBottom: "1px solid #e0e0e0 !important",
              }}>                  
            </TableCell>
          </TableRow>                        
        </TableFooter>

      </Table>
    </Box>
  );
}
